import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listSeedancePresetActors,
  listAdminSeedancePresetActors,
  createSeedancePresetActor,
  updateSeedancePresetActor,
  deleteSeedancePresetActor,
  reorderSeedancePresetActors,
  previewImportSeedancePresetActor,
  importSeedancePresetActor,
  bulkImportSeedancePresetActors,
  listSeedancePresetImportLogs,
} from './api';
import type {
  SeedancePresetActorFilters,
  CreateSeedancePresetActorRequest,
  UpdateSeedancePresetActorRequest,
  ImportSeedancePresetActorRequest,
} from './types';

const USER_QUERY_KEY = ['seedance-preset-actors'];
const ADMIN_QUERY_KEY = ['admin', 'seedance-preset-actors'];

export function useSeedancePresetActors(
  filters?: SeedancePresetActorFilters,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...USER_QUERY_KEY, filters || {}],
    queryFn: () => listSeedancePresetActors(filters),
    enabled: options?.enabled ?? true,
  });
}

export function useAdminSeedancePresetActors() {
  return useQuery({
    queryKey: ADMIN_QUERY_KEY,
    queryFn: listAdminSeedancePresetActors,
  });
}

export function useCreateSeedancePresetActor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateSeedancePresetActorRequest) => createSeedancePresetActor(request),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
      qc.invalidateQueries({ queryKey: USER_QUERY_KEY });
    },
  });
}

export function useUpdateSeedancePresetActor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      actorId, request,
    }: { actorId: string; request: UpdateSeedancePresetActorRequest }) =>
      updateSeedancePresetActor(actorId, request),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
      qc.invalidateQueries({ queryKey: USER_QUERY_KEY });
    },
  });
}

export function usePreviewImportSeedancePresetActor() {
  return useMutation({
    mutationFn: (byteplusAssetId: string) => previewImportSeedancePresetActor(byteplusAssetId),
  });
}

export function useImportSeedancePresetActor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: ImportSeedancePresetActorRequest) => importSeedancePresetActor(request),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
      qc.invalidateQueries({ queryKey: USER_QUERY_KEY });
    },
  });
}

export function useBulkImportSeedancePresetActors() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetIds, visibility }: {
      assetIds: string[];
      visibility?: 'private' | 'organization' | 'public';
    }) => bulkImportSeedancePresetActors(assetIds, visibility),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
      qc.invalidateQueries({ queryKey: USER_QUERY_KEY });
      qc.invalidateQueries({ queryKey: [...ADMIN_QUERY_KEY, 'import-logs'] });
    },
  });
}

export function useSeedancePresetImportLogs(
  params?: { limit?: number; status?: 'imported' | 'skipped' | 'failed' },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [...ADMIN_QUERY_KEY, 'import-logs', params || {}],
    queryFn: () => listSeedancePresetImportLogs(params),
    enabled: options?.enabled ?? true,
  });
}

export function useDeleteSeedancePresetActor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (actorId: string) => deleteSeedancePresetActor(actorId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
      qc.invalidateQueries({ queryKey: USER_QUERY_KEY });
    },
  });
}

export function useReorderSeedancePresetActors() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orders: { id: string; display_order: number }[]) =>
      reorderSeedancePresetActors(orders),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEY });
      qc.invalidateQueries({ queryKey: USER_QUERY_KEY });
    },
  });
}
